(() => {
  if (customElements.get('poker-collection')) return;

  class PokerCollection extends HTMLElement {
    async connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.cards = Array.from(this.querySelectorAll('[data-poker-card]'));
      if (!this.cards.length) return;
      this.active = Math.min(Math.max(Number(this.dataset.startIndex) || 0, 0), this.cards.length - 1);
      this.deck = this.querySelector('[data-poker-deck]');
      this.dots = this.querySelector('[data-poker-dots]');
      this.descriptions = Array.from(this.querySelectorAll('[data-poker-description]'));
      this.bindEvents();
      this.render();
      await this.loadMakes();
    }

    bindEvents() {
      this.secondaryUpdates = [];
      this.cards.forEach((card, index) => card.querySelector('[data-poker-select]').addEventListener('click', (event) => {
        if (Date.now() < (this.suppressClickUntil || 0)) { event.preventDefault(); event.stopPropagation(); return; }
        event.preventDefault();
        this.select(index);
      }));
      this.querySelectorAll('[data-poker-collection-link]').forEach((link) => link.addEventListener('click', (event) => {
        if (Date.now() < (this.suppressClickUntil || 0)) { event.preventDefault(); event.stopPropagation(); }
      }));
      this.addEventListener('click', (event) => {
        const makeLink = event.target.closest('[data-poker-make-link]');
        if (!makeLink || !this.contains(makeLink)) return;
        event.preventDefault();
        event.stopPropagation();
        window.location.assign(makeLink.href);
      });
      this.deck.querySelectorAll('img').forEach((image) => image.setAttribute('draggable', 'false'));
      this.querySelector('[data-poker-prev]')?.addEventListener('click', () => this.select(this.active - 1));
      this.querySelector('[data-poker-next]')?.addEventListener('click', () => this.select(this.active + 1));
      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.select(this.active - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.select(this.active + 1); }
      });
      this.querySelectorAll('.poker-collection__secondary-zone').forEach((zone) => {
        const cards = Array.from(zone.querySelectorAll('[data-secondary-card]'));
        const alwaysExpanded = zone.dataset.parentSuit === 'air_management';
        const styles = getComputedStyle(this);
        const desktopLimit = Number(styles.getPropertyValue('--poker-secondary-desktop')) || 7;
        const mobileLimit = Number(styles.getPropertyValue('--poker-secondary-mobile')) || 4;
        const toggle = zone.querySelector('[data-secondary-toggle]');
        const update = () => {
          if (alwaysExpanded) {
            cards.forEach((card) => card.classList.remove('is-over-limit'));
            return;
          }
          const limit = window.matchMedia('(max-width: 900px)').matches ? mobileLimit : desktopLimit;
          cards.forEach((card, index) => card.classList.toggle('is-over-limit', index >= limit));
          if (toggle) toggle.hidden = cards.length <= limit;
        };
        update();
        this.secondaryUpdates.push(update);
        toggle?.addEventListener('click', () => {
          const expanded = zone.classList.toggle('is-expanded');
          toggle.textContent = expanded ? 'COLLAPSE CATEGORIES ↑' : 'SHOW MORE CATEGORIES ↓';
          toggle.setAttribute('aria-expanded', String(expanded));
        });
      });
      this.querySelectorAll('[data-poker-make-toggle]').forEach((toggle) => toggle.addEventListener('click', (event) => {
        const zone = event.currentTarget.closest('.poker-collection__make-zone');
        const expanded = zone.classList.toggle('is-expanded');
        const count = event.currentTarget.dataset.makeCount || '';
        event.currentTarget.setAttribute('aria-expanded', String(expanded));
        event.currentTarget.innerHTML = `<span>${expanded ? 'COLLAPSE MAKES' : `SHOW ALL ${count} MAKES`}</span><i aria-hidden="true">⌄</i>`;
      }));
      let startX = 0; let startY = 0; let deltaX = 0; let deltaY = 0;
      this.deck.addEventListener('touchstart', (event) => {
        startX = event.touches[0].clientX; startY = event.touches[0].clientY; deltaX = 0; deltaY = 0;
      }, { passive: true });
      this.deck.addEventListener('touchmove', (event) => {
        deltaX = event.touches[0].clientX - startX; deltaY = event.touches[0].clientY - startY;
      }, { passive: true });
      this.deck.addEventListener('touchend', (event) => {
        const moved = Math.hypot(deltaX, deltaY);
        if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
          event.preventDefault();
          this.suppressClickUntil = Date.now() + 500;
          this.select(this.active + (deltaX < 0 ? 1 : -1));
        } else if (moved > 12) {
          this.suppressClickUntil = Date.now() + 300;
        }
        deltaX = 0; deltaY = 0;
      });
      this.deck.addEventListener('touchcancel', () => { deltaX = 0; deltaY = 0; });
      this.resizeHandler = () => {
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => {
          this.secondaryUpdates.forEach((update) => update());
          this.render();
        });
      };
      window.addEventListener('resize', this.resizeHandler, { passive: true });
      this.blockSelectHandler = (event) => {
        const card = event.target.closest?.('[data-poker-card]');
        if (card && this.contains(card)) this.select(Number(card.dataset.index));
      };
      this.addEventListener('shopify:block:select', this.blockSelectHandler);
    }

    disconnectedCallback() {
      if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
      cancelAnimationFrame(this.resizeFrame);
      if (this.blockSelectHandler) this.removeEventListener('shopify:block:select', this.blockSelectHandler);
    }

    parseMakePage(root = document) {
      const script = root.querySelector(`[data-poker-make-page][data-section-id="${this.dataset.sectionId}"]`);
      if (!script) return { page: 1, pages: 1, collectionUrl: '', brandCollections: [], products: [] };
      try { return JSON.parse(script.textContent); } catch (error) { return { page: 1, pages: 1, collectionUrl: '', brandCollections: [], products: [] }; }
    }

    async fetchMakePage(page) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set('page', String(page));
        const response = await fetch(url.toString(), { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Make data request failed');
        const root = new DOMParser().parseFromString(await response.text(), 'text/html');
        return this.parseMakePage(root).products || [];
      } catch (error) {
        console.warn('[Shop by Collection]', error);
        return [];
      }
    }

    normalizeMakes(value) {
      if (value == null) return [];
      if (Array.isArray(value)) return value.flatMap((item) => this.normalizeMakes(item));
      if (typeof value === 'object') {
        const preferred = value.label ?? value.name ?? value.value ?? value.title;
        return preferred == null ? [] : this.normalizeMakes(preferred);
      }
      return String(value).split(/[,;|\n]/).map((item) => item.trim()).filter(Boolean);
    }

    normalizedCollectionKey(value) {
      return String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/gi, '').toLowerCase();
    }

    collectionHandleForMake(make) {
      return String(make || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    }

    makeCollectionUrl(make, brandCollections = []) {
      const makeKey = this.normalizedCollectionKey(make);
      const makeHandle = this.collectionHandleForMake(make);
      const match = brandCollections.find((collection) => this.normalizedCollectionKey(collection.title) === makeKey)
        || brandCollections.find((collection) => collection.handle === makeHandle)
        || brandCollections.find((collection) => this.normalizedCollectionKey(collection.handle) === makeKey);
      return match?.url || `/collections/${makeHandle}`;
    }

    async loadMakes() {
      const target = this.querySelector('[data-poker-makes]');
      if (!target) return;
      const first = this.parseMakePage();
      let products = first.products || [];
      const brandCollections = first.brandCollections || [];
      if (first.pages > 1) {
        const requests = [];
        for (let page = 1; page <= first.pages; page += 1) if (page !== first.page) requests.push(this.fetchMakePage(page));
        (await Promise.all(requests)).forEach((pageProducts) => products.push(...pageProducts));
      }
      const makes = new Map();
      products.forEach((product) => this.normalizeMakes(product.make).concat(this.normalizeMakes(product.makeText)).forEach((make) => {
        const key = make.toLocaleLowerCase();
        if (!makes.has(key)) makes.set(key, make);
      }));
      const sorted = Array.from(makes.values()).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
      target.replaceChildren();
      sorted.forEach((make) => {
        const item = document.createElement('a');
        item.className = 'poker-collection__make-item';
        item.href = this.makeCollectionUrl(make, brandCollections);
        item.dataset.pokerMakeLink = '';
        item.setAttribute('aria-label', `View Air Suspension products for ${make}`);
        item.innerHTML = `<strong></strong><span aria-hidden="true">•</span>`;
        item.querySelector('strong').textContent = make;
        target.append(item);
      });
      if (!sorted.length) {
        const empty = document.createElement('p'); empty.className = 'poker-collection__child-empty'; empty.textContent = 'No vehicle makes are configured in custom.make yet.'; target.append(empty);
      }
      const count = this.querySelector('[data-poker-make-count]');
      if (count) count.textContent = `${sorted.length} makes`;
      const toggle = this.querySelector('[data-poker-make-toggle]');
      if (toggle) {
        const updateToggle = () => { toggle.hidden = sorted.length <= (window.matchMedia('(max-width: 900px)').matches ? 6 : 28); };
        updateToggle();
        window.addEventListener('resize', updateToggle);
        toggle.dataset.makeCount = String(sorted.length);
        toggle.setAttribute('aria-expanded', 'false');
        toggle.innerHTML = `<span>SHOW ALL ${sorted.length} MAKES</span><i aria-hidden="true">⌄</i>`;
      }
    }

    select(index) {
      this.active = (index + this.cards.length) % this.cards.length;
      this.render();
    }

    render() {
      const mobile = window.matchMedia('(max-width: 900px)').matches;
      const styles = getComputedStyle(this);
      const configuredSpread = Number(styles.getPropertyValue(mobile ? '--poker-spread-mobile' : '--poker-spread-desktop')) || (mobile ? 165 : 220);
      this.cards.forEach((card, index) => {
        const distance = index - this.active;
        card.classList.toggle('is-active', index === this.active);
        const trigger = card.querySelector('[data-poker-select]');
        trigger?.setAttribute('aria-pressed', String(index === this.active));
        if (index === this.active) trigger?.setAttribute('aria-current', 'true'); else trigger?.removeAttribute('aria-current');
        card.style.zIndex = String(10 - Math.abs(distance));
        if (mobile) {
          card.style.transform = `translateX(calc(-50% + ${distance * configuredSpread}px)) rotate(${distance * 2.5}deg) scale(${index === this.active ? 1 : .84})`;
          card.style.opacity = Math.abs(distance) > 2 ? '0' : '1';
          card.style.pointerEvents = Math.abs(distance) > 2 ? 'none' : 'auto';
        } else {
          const maximumDistance = Math.max(this.active, this.cards.length - 1 - this.active, 1);
          const availableWidth = Math.max((this.deck.clientWidth - 280) / 2, 300);
          const spread = Math.min(configuredSpread, availableWidth / maximumDistance);
          card.style.transform = `translateX(calc(-50% + ${distance * spread}px)) translateY(${index === this.active ? -10 : Math.abs(distance) * 6}px) rotate(${distance * 3.1}deg) scale(${index === this.active ? 1.07 : 1})`;
          card.style.opacity = '1';
          card.style.pointerEvents = 'auto';
        }
      });
      this.renderDots();
      this.descriptions.forEach((description, index) => { description.hidden = index !== this.active; });
    }

    renderDots() {
      if (!this.dots) return;
      this.dots.replaceChildren(...this.cards.map((_, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `poker-collection__dot${index === this.active ? ' is-active' : ''}`;
        button.setAttribute('aria-label', `Show collection ${index + 1}`);
        button.addEventListener('click', () => this.select(index));
        return button;
      }));
    }
  }

  customElements.define('poker-collection', PokerCollection);
})();
